import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, MagnifyingGlass, Funnel, CaretLeft, CaretRight } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AdminChat from '../../pages/AdminChat';
import HandlerChat from '../../pages/HandlerChat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
const API = `${BACKEND_URL}/api`;

export default function ServiceTimelines({ onBack, onOpenChat }) {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('deposit');
  const [dateRange, setDateRange] = useState('month');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedClient, setSelectedClient] = useState(null);

  // Embedded chat state
  const [viewingRoomId, setViewingRoomId] = useState(null);

  // Search, Sort & Pagination States
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState('newest'); // 'newest' | 'oldest' | 'tr_high' | 'tr_low' | 'form_id'
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        type: activeTab,
        status: statusFilter,
        range: dateRange,
      };
      if (dateRange === 'custom' && customStartDate && customEndDate) {
        params.startDate = customStartDate;
        params.endDate = customEndDate;
      }
      const res = await axios.get(`${API}/chat/service-timelines`, { params });
      setRequests(res.data || []);
      setSelectedIds(new Set());
      setCurrentPage(1);
    } catch (err) {
      console.error('Failed to fetch service timelines:', err);
      toast.error('Failed to load service timelines data');
    } finally {
      setLoading(false);
    }
  }, [activeTab, statusFilter, dateRange, customStartDate, customEndDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset to page 1 on search / sort / filter change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, sortOption, selectedClient]);

  const handleSelectAll = () => {
    if (selectedIds.size === paginatedRequests.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(paginatedRequests.map(r => r.id)));
    }
  };

  const handleClearSelection = () => {
    setSelectedIds(new Set());
  };

  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const handleBatchClose = async () => {
    if (selectedIds.size === 0) {
      toast.error('Please select at least one request');
      return;
    }
    try {
      await axios.post(`${API}/chat/service-timelines/batch-close`, {
        ids: Array.from(selectedIds)
      });
      toast.success(`${selectedIds.size} request(s) closed successfully`);
      fetchData();
    } catch (err) {
      toast.error('Failed to close selected requests');
    }
  };

  const handleBatchDelete = async () => {
    if (selectedIds.size === 0) {
      toast.error('Please select at least one request');
      return;
    }
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.size} request(s)?`)) {
      return;
    }
    try {
      await axios.post(`${API}/chat/service-timelines/batch-delete`, {
        ids: Array.from(selectedIds)
      });
      toast.success(`${selectedIds.size} request(s) deleted successfully`);
      fetchData();
    } catch (err) {
      toast.error('Failed to delete selected requests');
    }
  };

  const computeTR = (req) => {
    if (req.status === 'pending' || req.status === 'open') {
      return '--';
    }
    if (!req.created_at || !req.updated_at) return '0 min';
    const start = new Date(req.created_at);
    const end = new Date(req.updated_at);
    const diffMins = Math.max(0, Math.round((end - start) / (1000 * 60)));
    return `${diffMins} min`;
  };

  const computeTRMinutes = (req) => {
    if (!req.created_at || !req.updated_at) return 0;
    const start = new Date(req.created_at);
    const end = new Date(req.updated_at);
    return Math.max(0, Math.round((end - start) / (1000 * 60)));
  };

  /**
   * Click handler to open chat for a user / request
   */
  const handleOpenChat = async (req) => {
    let targetRoomId = req.room_id;

    if (!targetRoomId && req.customer_id) {
      try {
        const startRes = await axios.post(`${API}/chat/start`, {
          customer_id: req.customer_id,
          chat_type: req.type || activeTab || 'deposit'
        });
        targetRoomId = startRes.data?.room_id || startRes.data?.room?.id;
      } catch (e) {
        console.error('Failed to start/find room:', e);
      }
    }

    if (targetRoomId) {
      if (onOpenChat) {
        onOpenChat(targetRoomId, req.type || activeTab, req.customer_id);
      } else {
        setViewingRoomId(targetRoomId);
      }
    } else {
      toast.error('Could not find chat room for this user');
    }
  };

  // Aggregate Client-Wise Summary
  const clientSummaryMap = {};
  requests.forEach(req => {
    const rawName = req.customer_username || 'Unknown Client';
    const mbStr = (req.mb_setup && req.mb_setup !== 'ALL MB' && req.mb_setup !== 'all') ? req.mb_setup : '';
    const displayName = mbStr ? `${rawName} (${mbStr})` : rawName;

    if (!clientSummaryMap[rawName]) {
      clientSummaryMap[rawName] = { total: 0, open: 0, closed: 0, canceled: 0, displayName, rawName, mbStr };
    }
    clientSummaryMap[rawName].total += 1;
    if (req.status === 'pending' || req.status === 'open') {
      clientSummaryMap[rawName].open += 1;
    } else if (req.status === 'closed') {
      clientSummaryMap[rawName].closed += 1;
    } else if (req.status === 'canceled' || req.status === 'cancelled') {
      clientSummaryMap[rawName].canceled += 1;
    }
  });

  // 1. Filter ENTIRE dataset by summary client selection & quick search query
  const filteredRequests = requests.filter(req => {
    if (selectedClient) {
      const clientName = req.customer_username || 'Unknown Client';
      if (clientName !== selectedClient) return false;
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      const formIdMatch = (req.form_id || '').toLowerCase().includes(q);
      const nameMatch = (req.customer_username || '').toLowerCase().includes(q);
      const phoneMatch = (req.customer_phone || '').toLowerCase().includes(q);
      const mbMatch = (req.mb_setup || '').toLowerCase().includes(q);
      if (!formIdMatch && !nameMatch && !phoneMatch && !mbMatch) return false;
    }
    return true;
  });

  // 2. Sort ENTIRE filtered dataset
  const sortedRequests = [...filteredRequests].sort((a, b) => {
    if (sortOption === 'oldest') {
      return new Date(a.created_at || 0) - new Date(b.created_at || 0);
    }
    if (sortOption === 'tr_high') {
      return computeTRMinutes(b) - computeTRMinutes(a);
    }
    if (sortOption === 'tr_low') {
      return computeTRMinutes(a) - computeTRMinutes(b);
    }
    if (sortOption === 'form_id') {
      const aNum = parseInt((a.form_id || '').replace(/\D/g, ''), 10) || 0;
      const bNum = parseInt((b.form_id || '').replace(/\D/g, ''), 10) || 0;
      return bNum - aNum;
    }
    // Default: newest first
    return new Date(b.created_at || 0) - new Date(a.created_at || 0);
  });

  // 3. Paginate the sorted & filtered dataset
  const totalItems = sortedRequests.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const validCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (validCurrentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalItems);
  const paginatedRequests = sortedRequests.slice(startIndex, endIndex);

  // Avatar color using fixed palette based on name hash
  const getAvatarColor = (name) => {
    const colors = ['#1e88e5', '#e65100', '#2e7d32', '#6a1b9a', '#c62828', '#00838f', '#ef6c00', '#4527a0', '#1565c0', '#ad1457'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash += name.charCodeAt(i);
    return colors[Math.abs(hash) % colors.length];
  };

  const tabs = [
    { key: 'deposit', label: 'Deposit Requests' },
    { key: 'withdrawal', label: 'Withdraw Requests' },
    { key: 'support', label: 'Support Requests' },
  ];

  const dateRangeOptions = [
    { key: 'today', label: 'Today' },
    { key: 'week', label: 'This Week' },
    { key: 'month', label: 'This Month' },
    { key: 'custom', label: 'Custom' },
  ];

  const statusOptions = [
    { key: 'all', label: 'All' },
    { key: 'open', label: 'Open' },
    { key: 'closed', label: 'Closed' },
    { key: 'canceled', label: 'Cancelled' },
  ];

  if (viewingRoomId) {
    const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';
    if (isAdmin) {
      return (
        <AdminChat
          embeddedRoomId={viewingRoomId}
          onBackToList={() => setViewingRoomId(null)}
        />
      );
    }
    return (
      <HandlerChat
        embeddedRoomId={viewingRoomId}
        onBackToList={() => setViewingRoomId(null)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col w-full text-xs" style={{ fontFamily: "'Segoe UI', sans-serif" }}>
      {/* ─── Top Header Bar (Green Theme) ─── */}
      <div className="bg-[#10b981] text-white px-5 py-3.5 flex items-center justify-between shadow-md flex-shrink-0 w-full">
        <div className="flex items-center gap-3">
          {onBack && (
            <button
              onClick={onBack}
              className="p-1.5 hover:bg-white/20 rounded-lg transition-colors focus:outline-none cursor-pointer"
              title="Back"
            >
              <ArrowLeft size={18} weight="bold" />
            </button>
          )}
          <div>
            <h1 className="text-base font-bold tracking-wide">Service Timelines</h1>
            <p className="text-[11px] text-emerald-100 font-medium">Control Center · Admin Granted Access</p>
          </div>
        </div>
      </div>

      {/* ─── Tabs ─── */}
      <div className="flex border-b border-gray-200 bg-white">
        {tabs.map(tab => {
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => { setActiveTab(tab.key); setSelectedClient(null); setSearchQuery(''); }}
              className="flex-1 text-center py-3 text-[13px] font-medium transition-all cursor-pointer relative"
              style={{
                color: isActive ? '#111' : '#888',
                borderBottom: isActive ? '2px solid #111' : '2px solid transparent',
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* ─── Filters Row ─── */}
      <div className="px-4 pt-4 pb-2 flex items-center justify-between flex-wrap gap-2 bg-white">
        <div className="flex items-center gap-2 flex-wrap">
          {/* Date Range Pills */}
          {dateRangeOptions.map(opt => (
            <button
              key={opt.key}
              onClick={() => setDateRange(opt.key)}
              className="px-3 py-1 text-[11px] font-medium rounded-full border transition-all cursor-pointer"
              style={{
                backgroundColor: dateRange === opt.key ? '#111' : '#fff',
                color: dateRange === opt.key ? '#fff' : '#444',
                borderColor: dateRange === opt.key ? '#111' : '#ccc',
              }}
            >
              {opt.label}
            </button>
          ))}

          {/* Custom Date Inputs */}
          {dateRange === 'custom' && (
            <div className="flex items-center gap-1.5 ml-1">
              <input
                type="date"
                value={customStartDate}
                onChange={e => setCustomStartDate(e.target.value)}
                className="border border-gray-300 rounded px-2 py-0.5 text-[11px] focus:outline-none focus:border-emerald-500"
              />
              <span className="text-gray-400 text-[11px]">to</span>
              <input
                type="date"
                value={customEndDate}
                onChange={e => setCustomEndDate(e.target.value)}
                className="border border-gray-300 rounded px-2 py-0.5 text-[11px] focus:outline-none focus:border-emerald-500"
              />
            </div>
          )}
        </div>

        {/* Refresh Button */}
        <button
          onClick={fetchData}
          className="text-[11px] font-medium text-[#10b981] hover:underline transition-all cursor-pointer"
        >
          Refresh
        </button>
      </div>

      {/* ─── Status Filter Pills ─── */}
      <div className="px-4 pb-3 flex items-center gap-2 flex-wrap bg-white border-b border-gray-100">
        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mr-1">STATUS:</span>
        {statusOptions.map(opt => (
          <button
            key={opt.key}
            onClick={() => setStatusFilter(opt.key)}
            className="px-2.5 py-0.5 text-[11px] font-medium rounded-full border transition-all cursor-pointer"
            style={{
              backgroundColor: statusFilter === opt.key ? '#111' : '#fff',
              color: statusFilter === opt.key ? '#fff' : '#555',
              borderColor: statusFilter === opt.key ? '#111' : '#ddd',
            }}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* ─── Main Content ─── */}
      <div className="flex-1 overflow-y-auto">
        {/* ─── CLIENT-WISE SUMMARY (Avatars Grid) ─── */}
        <div className="px-4 pt-4 pb-3">
          <h2 className="text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-2.5">
            CLIENT-WISE SUMMARY
          </h2>

          {loading ? (
            <div className="text-[12px] text-gray-400 py-4">Loading summary...</div>
          ) : Object.keys(clientSummaryMap).length === 0 ? (
            <div className="text-[12px] text-gray-400 py-2">No summary available.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {Object.entries(clientSummaryMap).map(([rawClientName, counts]) => {
                const isSelected = selectedClient === rawClientName;
                const avatarBg = getAvatarColor(rawClientName);
                const initial = (rawClientName.charAt(0) || 'C').toUpperCase();

                return (
                  <div
                    key={rawClientName}
                    onClick={() => setSelectedClient(isSelected ? null : rawClientName)}
                    className="flex items-center gap-3 p-2.5 rounded-lg border bg-white transition-all cursor-pointer hover:shadow-sm"
                    style={{
                      borderColor: isSelected ? '#10b981' : '#e5e7eb',
                      backgroundColor: isSelected ? '#f0fdf4' : '#ffffff',
                    }}
                  >
                    {/* Circle Avatar */}
                    <div
                      className="w-8 h-8 rounded-full text-white font-bold flex items-center justify-center flex-shrink-0 text-xs shadow-xs"
                      style={{ backgroundColor: avatarBg }}
                    >
                      {initial}
                    </div>

                    {/* Client Name + Sub-stats */}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-800 truncate text-[12px]">{counts.displayName}</div>
                      <div className="text-[10px] text-gray-500 flex items-center gap-1.5 flex-wrap mt-0.5">
                        <span>Total: <strong className="text-gray-700">{counts.total}</strong></span>
                        <span className="text-orange-600">Open: <strong>{counts.open}</strong></span>
                        <span className="text-emerald-600">Closed: <strong>{counts.closed}</strong></span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ─── INDIVIDUAL REQUESTS ─── */}
        <div className="px-4 pt-2 pb-6">
          {/* Section Header with Search, Sort & Batch Actions */}
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2.5 bg-white p-3 rounded-lg border border-gray-200 shadow-2xs">
            {/* Title & Selected Filter Badge */}
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-[12px] font-bold text-gray-700 uppercase tracking-wide">
                INDIVIDUAL REQUESTS ({totalItems})
              </h2>
              {selectedClient && (
                <span className="normal-case bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-md text-[11px] flex items-center gap-1">
                  Client: <strong>{clientSummaryMap[selectedClient]?.displayName || selectedClient}</strong>
                  <button
                    onClick={() => setSelectedClient(null)}
                    className="hover:text-emerald-900 ml-1 font-bold cursor-pointer"
                  >
                    ×
                  </button>
                </span>
              )}
            </div>

            {/* Quick Search & Sort By */}
            <div className="flex items-center gap-2 flex-wrap">
              {/* Entire Table Search Input */}
              <div className="relative flex items-center">
                <MagnifyingGlass size={14} className="absolute left-2.5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search client or Form ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 pr-3 py-1 text-[11px] border border-gray-300 rounded-md focus:outline-none focus:border-emerald-500 w-[180px] sm:w-[220px] bg-slate-50 focus:bg-white transition-all"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2 text-gray-400 hover:text-gray-600 text-xs font-bold cursor-pointer"
                  >
                    ×
                  </button>
                )}
              </div>

              {/* Sort Dropdown */}
              <div className="flex items-center gap-1 text-[11px] border border-gray-300 rounded-md px-2 py-1 bg-white">
                <Funnel size={13} className="text-gray-500" />
                <span className="text-gray-500 font-medium">Sort:</span>
                <select
                  value={sortOption}
                  onChange={(e) => setSortOption(e.target.value)}
                  className="bg-transparent focus:outline-none font-semibold text-gray-700 cursor-pointer"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="tr_high">TR: High to Low</option>
                  <option value="tr_low">TR: Low to High</option>
                  <option value="form_id">Form ID</option>
                </select>
              </div>
            </div>

            {/* Batch Action Buttons */}
            <div className="flex items-center gap-2 text-[11px] border-l pl-3 border-gray-200">
              <button
                onClick={handleSelectAll}
                className="text-gray-600 hover:text-gray-900 font-medium transition-colors cursor-pointer"
              >
                Select All
              </button>
              <button
                onClick={handleClearSelection}
                className="text-gray-400 hover:text-gray-600 font-medium transition-colors cursor-pointer"
              >
                Clear ({selectedIds.size})
              </button>
              <button
                onClick={handleBatchClose}
                disabled={selectedIds.size === 0}
                className="px-2.5 py-1 text-white font-semibold rounded text-[11px] transition-all cursor-pointer disabled:opacity-40"
                style={{ backgroundColor: '#00a884' }}
              >
                Close range
              </button>
              <button
                onClick={handleBatchDelete}
                disabled={selectedIds.size === 0}
                className="px-2.5 py-1 bg-red-600 text-white font-semibold rounded text-[11px] hover:bg-red-700 transition-all cursor-pointer disabled:opacity-40"
              >
                Delete range
              </button>
            </div>
          </div>

          {/* Requests List */}
          {paginatedRequests.length === 0 ? (
            <div className="text-[12px] text-gray-400 py-8 text-center bg-white rounded-lg border border-gray-200">
              {searchQuery ? `No requests match "${searchQuery}"` : 'No requests found for current filters.'}
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden divide-y divide-gray-100 shadow-2xs">
              {paginatedRequests.map((req) => {
                const isChecked = selectedIds.has(req.id);
                const tr = computeTR(req);
                const rawClientName = req.customer_username || 'Client';
                const mbStr = (req.mb_setup && req.mb_setup !== 'ALL MB' && req.mb_setup !== 'all') ? req.mb_setup : '';
                const displayClientName = mbStr ? `${rawClientName} (${mbStr})` : rawClientName;
                const isClosed = req.status === 'closed';
                const isOpen = req.status === 'pending' || req.status === 'open';
                const isCanceled = req.status === 'canceled' || req.status === 'cancelled';

                return (
                  <div
                    key={req.id}
                    className="flex items-center justify-between px-3 py-2.5 hover:bg-slate-50 transition-colors"
                  >
                    {/* Left: checkbox + form_id + client name (CLICK TO OPEN CHAT) */}
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggleSelect(req.id)}
                        className="w-[15px] h-[15px] accent-emerald-600 cursor-pointer"
                      />
                      <span
                        onClick={() => handleOpenChat(req)}
                        className="text-[13px] font-bold text-gray-800 min-w-[48px] hover:text-emerald-600 cursor-pointer transition-colors"
                        title="Click to open chat"
                      >
                        {req.form_id || 'N/A'}
                      </span>
                      <span
                        onClick={() => handleOpenChat(req)}
                        className="text-[13px] text-blue-600 hover:text-blue-800 font-medium hover:underline cursor-pointer transition-colors flex items-center gap-1"
                        title={`Click to open chat with ${displayClientName}`}
                      >
                        {displayClientName}
                      </span>
                    </div>

                    {/* Right: status dot + text + TR */}
                    <div className="flex items-center gap-3 text-[12px]">
                      {isClosed && (
                        <>
                          <span className="flex items-center gap-1 font-semibold" style={{ color: '#2e7d32' }}>
                            <span className="w-[6px] h-[6px] rounded-full inline-block" style={{ backgroundColor: '#2e7d32' }} />
                            Closed
                          </span>
                          <span className="text-gray-400 text-[11px] font-mono">TR: {tr}</span>
                        </>
                      )}
                      {isOpen && (
                        <>
                          <span className="flex items-center gap-1 font-semibold" style={{ color: '#e65100' }}>
                            <span className="w-[6px] h-[6px] rounded-full inline-block" style={{ backgroundColor: '#e65100' }} />
                            Open
                          </span>
                          <span className="text-gray-400 text-[11px] font-mono">TR: {tr}</span>
                        </>
                      )}
                      {isCanceled && (
                        <>
                          <span className="font-semibold" style={{ color: '#c62828' }}>
                            ✕ Cancelled
                          </span>
                          <span className="text-gray-400 text-[11px] font-mono">TR: {tr}</span>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ─── Pagination Footer Bar ─── */}
          {totalItems > 0 && (
            <div className="flex items-center justify-between mt-3 px-3 py-2 bg-white border border-gray-200 rounded-lg flex-wrap gap-2 text-[11px]">
              {/* Items Counter Info */}
              <div className="text-gray-500 font-medium">
                Showing <strong className="text-gray-700">{startIndex + 1}</strong> – <strong className="text-gray-700">{endIndex}</strong> of <strong className="text-gray-700">{totalItems}</strong> requests
              </div>

              {/* Pagination Controls */}
              <div className="flex items-center gap-3">
                {/* Per Page Selector */}
                <div className="flex items-center gap-1 text-gray-500 font-medium">
                  <span>Per page:</span>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="border border-gray-300 rounded px-1.5 py-0.5 bg-white text-gray-700 focus:outline-none cursor-pointer"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>

                {/* Page Buttons */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={validCurrentPage <= 1}
                    className="p-1 rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-40 cursor-pointer transition-colors"
                    title="Previous Page"
                  >
                    <CaretLeft size={13} weight="bold" />
                  </button>

                  <span className="px-2 font-semibold text-gray-700">
                    Page {validCurrentPage} of {totalPages}
                  </span>

                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={validCurrentPage >= totalPages}
                    className="p-1 rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-40 cursor-pointer transition-colors"
                    title="Next Page"
                  >
                    <CaretRight size={13} weight="bold" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
